<template>
  <div class="p-6 min-w-min h-full flex flex-col bg-white rounded-2xl">
    <!-- title and search bar -->
    <div class="flex flex-row items-center justify-between">
      <p class="text-3xl font-bold">Request Members</p>
      <div class="flex flex-row items-center justify-between space-x-4">
        <div class="relative group">
          <input type="text" v-model="searchText" class="bg-light-gray px-4 py-3 rounded-full font-sans"
            placeholder="Search for members" v-on:keyup.enter="handleSearch" />
          <MagnifyingGlassIcon class="absolute bottom-3 right-3 w-6 h-6 text-gray" />
        </div>
        <!-- filter by subscription -->
        <div class="relative cursor-pointer">
          <div>
            <listbox v-model="subFilter">
              <ListboxButton>
                <div class="flex flex-row space-x-2 items-center">
                  <p class="text-gray">Subscription:</p>
                  <p v-if="subFilter.name" class="text-blue">
                    {{ subFilter.name }}
                  </p>
                  <p v-else class="text-blue">All</p>
                  <ChevronDownIcon class="w-4 h-4 text-gray" />
                </div>
              </ListboxButton>
              <transition enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-out" leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <ListboxOptions class="
                          absolute
                          right-0
                          py-2
                          w-full
                          bg-white
                          rounded-lg
                          shadow-md
                          border border-light-gray
                          min-w-max
                        ">
                  <ListboxOption value="" @click="handleSearch" class="hover:bg-light-gray px-4 py-2 font-sans font-md">
                    All</ListboxOption>
                  <ListboxOption v-for="option in subFilterOption" :key="option.key" :value="option" @click="handleSearch"
                    class="
                            hover:bg-light-gray
                            px-4
                            py-2
                            font-sans font-md
                            min-w-max
                          ">
                    {{ option.name }}
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </listbox>
          </div>
        </div>
        <!-- filter by status -->
        <div class="relative cursor-pointer">
          <div>
            <listbox v-model="statusFilter">
              <ListboxButton>
                <div class="flex flex-row space-x-2 items-center">
                  <p class="text-gray">Status:</p>
                  <p class="text-blue">{{ statusFilter.name }}</p>
                  <ChevronDownIcon class="w-4 h-4 text-gray" />
                </div>
              </ListboxButton>
              <transition enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-out" leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <ListboxOptions class="
                          absolute
                          py-2
                          w-full
                          bg-white
                          rounded-lg
                          shadow-md
                          border border-light-gray
                          z-10
                        ">
                  <ListboxOption v-for="option in statusFilterOption" :key="option.key" :value="option"
                    @click="handleSearch" class="
                            hover:bg-light-gray
                            px-4
                            py-2
                            font-sans font-md
                            min-w-max
                          ">
                    {{ option.name }}
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </listbox>
          </div>
        </div>
      </div>
    </div>
    <!-- body -->
    <div>
      <table class="table-auto w-full font-light">
        <thead class="border-b-2 border-light-gray">
          <tr class="text-left text-gray text-md">
            <th class="py-2 font-normal">Profile</th>
            <th class="py-2 font-normal">Name</th>
            <th class="py-2 font-normal">Phone</th>
            <th class="py-2 font-normal">Email</th>
            <th class="py-2 font-normal text-center">Membership Type</th>
            <th class="p-2 font-normal">Payment</th>
            <th class="py-2 font-normal text-center">Status</th>
            <th class="py-2 font-normal text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="member in members" :key="member.id" class="text-lg">
            <td>
              <img class="rounded-full w-14 h-14 object-cover my-2" :src="member.profile" alt="" />
            </td>
            <td>{{ member.first_name }} {{ member.last_name }}</td>
            <td>{{ member.phone_number }}</td>
            <td>{{ member.email }}</td>
            <td>
              <div v-if="member.latest_subscription" class="flex justify-center">
                <p class="text-blue">
                  {{ member.latest_subscription.subscription.name }}
                </p>
              </div>
            </td>
            <td>
              <div @click="handleViewReciept(member)"
                class="flex items-center space-x-2 select-none cursor-pointer group">
                <EyeIcon class="w-6 h-6 text-blue" />
                <p class="text-blue">view</p>
              </div>
            </td>
            <!-- member status -->
            <td>
              <div class="flex justify-center">
                <div v-if="member.status == '-1'" class="px-4 py-1 bg-light-red text-red rounded-full w-max">
                  <p>Rejected</p>
                </div>
                <div v-if="member.status == '0'" class="px-4 py-1 bg-light-red text-red rounded-full w-max">
                  <p>Inactive</p>
                </div>
                <div v-if="member.status == '1'" class="px-4 py-1 bg-light-gray text-gray rounded-full w-max">
                  <p>In Review</p>
                </div>
                <div v-if="member.status == '2'" class="px-4 py-1 bg-light-blue text-blue rounded-full w-max">
                  <p>Active</p>
                </div>
              </div>
            </td>
            <td>
              <div class="flex justify-center relative">
                <Menu>
                  <MenuButton>
                    <EllipsisVerticalIcon class="w-8 h-8 text-black hover:text-blue cursor-pointer" />
                  </MenuButton>
                  <MenuItems class="
                            p-1
                            space-y-1
                            absolute
                            top-8
                            right-8
                            z-50
                            bg-white
                            border border-light-gray
                            shadow-md
                            rounded-lg
                            w-12/12
                          ">
                    <MenuItem @click="handleViewSlideOver(member)"
                      class="hover:bg-light-blue m-1 rounded-md cursor-pointer">
                    <div class="flex flex-row items-center px-4 py-2 min-w-max">
                      <EyeIcon class="text-blue w-5 h-5 mr-4" />
                      <p class="text-blue">View Member Detail</p>
                    </div>
                    </MenuItem>
                    <MenuItem @click="handleDeactivate(member.id)" v-if="member.status == '0'"
                      class="hover:bg-light-blue m-1 rounded-md cursor-pointer">
                    <div class="flex flex-row items-center px-4 py-2 min-w-min">
                      <UserPlusIcon class="text-blue w-5 h-5 mr-4" />
                      <p class="text-blue">Reactivate this account</p>
                    </div>
                    </MenuItem>
                    <MenuItem @click="handleUpdateStatus(member.id, '2')"
                      class="hover:bg-light-blue m-1 rounded-md cursor-pointer">
                    <div class="flex flex-row items-center px-4 py-2 min-w-max">
                      <UserPlusIcon class="text-blue w-5 h-5 mr-4" />
                      <p class="text-blue">Confirm Member</p>
                    </div>
                    </MenuItem>
                    <MenuItem v-if="member.status == '1'" @click="handleUpdateStatus(member.id, '-1')"
                      class="hover:bg-light-blue m-1 rounded-md cursor-pointer">
                    <div class="flex flex-row items-center px-4 py-2 min-w-min">
                      <UserMinusIcon class="text-red w-5 h-5 mr-4" />
                      <p class="text-red">Reject</p>
                    </div>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- loading animation -->
      <div class="
                min-h-full
                flex flex-col
                space-y-4
                items-center
                justify-center
                mt-24
              " v-if="isLoading">
        <div class="animate-ping w-12 h-12 bg-light-gray rounded-full"></div>
        <p class="text-gray text-lg font-sans font-light">Loading data</p>
      </div>
      <!-- no data icon -->
      <div class="min-h-full flex items-center justify-center mt-24" v-if="!members.length && !isLoading">
        <div class="flex flex-col">
          <div class="flex justify-center">
            <InboxIcon class="text-gray w-12 h-12 md:w-24 md:h-24" />
          </div>
          <p class="text-gray text-2xl font-sans font-light">No data found!</p>
        </div>
      </div>
    </div>
    <div class="mt-auto flex justify-end">
      <BasePagination :pageCount="membersMeta.last_page" @navigate-to-page="handlePagination($event)" />
    </div>
  </div>
  <!-- view detail Slide over -->
  <SideBar v-show="viewSlideOver" @close-modal="handleViewSlideOver('')">
    <div class="w-full h-full flex flex-col content-between">
      <h1 class="text-blue text-2xl">Requested Member Detail</h1>
      <div class="flex flex-col space-y-4 py-8">
        <div class="flex justify-center">
          <img :src="tempData.profile" class="w-2/4 rounded-full square-img object-cover" alt="" />
        </div>
        <!-- first_name and last name -->
        <div class="flex flex-row mb-2 space-x-2">
          <div class="flex flex-col w-1/2">
            <p class="text-gray font-light mb-2">First Name</p>
            <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
              {{ tempData.first_name }}
            </p>
          </div>
          <div class="flex flex-col w-1/2">
            <p class="text-gray font-light mb-2">Last Name</p>
            <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
              {{ tempData.last_name }}
            </p>
          </div>
        </div>
        <!-- gender and title -->
        <div class="flex flex-row mb-2 space-x-2">
          <div class="flex flex-col w-1/2">
            <p class="text-gray font-light mb-2">Gender</p>
            <p v-if="tempData.gender == '0'" class="text-lg font-sans p-2 bg-light-gray rounded-lg">
              Male
            </p>
            <p v-if="tempData.gender == '1'" class="text-lg font-sans p-2 bg-light-gray rounded-lg">
              Female
            </p>
            <p v-if="tempData.gender == '2'" class="text-lg font-sans p-2 bg-light-gray rounded-lg">
              Other
            </p>
          </div>
          <div class="flex flex-col w-1/2">
            <p class="text-gray font-light mb-2">Title</p>
            <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
              {{ tempData.title }}
            </p>
          </div>
        </div>
        <!-- dob -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Date of Birth</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ moment(tempData.dob).format("DD MMMM YYYY") }}
          </p>
        </div>
        <!-- citizenship -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">citizenship</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.citizenship }}
          </p>
        </div>
        <!-- phone number -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Phone Number</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.phone_number }}
          </p>
        </div>
        <!-- buss phone number -->
        <div v-if="tempData.business_phone" class="flex flex-col">
          <p class="text-gray font-light mb-2">Business Phone</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.business_phone }}
          </p>
        </div>
        <!-- email -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Email</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.email }}
          </p>
        </div>
        <!-- buss email -->
        <div v-if="tempData.business_email" class="flex flex-col">
          <p class="text-gray font-light mb-2">Business Email</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.business_email }}
          </p>
        </div>
        <!-- address -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Business Email</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.home_address }}
          </p>
        </div>
        <!-- workplace -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">
            Name of Oraganization/Workplace
          </p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.name_of_organization_workplace }}
          </p>
        </div>
        <!-- job title -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Job Title</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.job_title }}
          </p>
        </div>
        <!-- other job -->
        <div v-if="tempData.job_other" class="flex flex-col">
          <p class="text-gray font-light mb-2">Other Job</p>
          <p class="text-lg font-sans p-2 fbg-light-gray rounded-lg">
            {{ tempData.job_other }}
          </p>
        </div>
        <!-- workplace -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">
            Name of Oraganization/Workplace
          </p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.name_of_organization_workplace }}
          </p>
        </div>
        <!-- country of university -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Country of University Attended</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.country_of_university_attended }}
          </p>
        </div>
        <!-- name of university -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Country of University Attended</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.name_of_university_attended }}
          </p>
        </div>
        <!-- highest qualification -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Name of University Attended</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.highest_qualification }}
          </p>
        </div>
        <!-- major -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Major</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.major }}
          </p>
        </div>
        <!-- date of comencement -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Date of Commencement</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ moment(tempData.date_of_commencement).format("DD MMMM YYYY") }}
          </p>
        </div>
        <!-- date of completion -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Date of Completion</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ moment(tempData.date_of_completion).format("DD MMMM YYYY") }}
          </p>
        </div>
        <!-- Funding -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Funding Scheme</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.funding_scheme }}
          </p>
        </div>
        <!-- scholarship -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Funding Scheme</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.scholarship_name }}
          </p>
        </div>
        <!-- member -->
        <div class="flex flex-col">
          <p class="text-gray font-light mb-2">Member Category</p>
          <p class="text-lg font-sans p-2 bg-light-gray rounded-lg">
            {{ tempData.membership_category }}
          </p>
        </div>
      </div>
    </div>
  </SideBar>
  <!-- view payment reciept side bar -->
  <SideBar v-show="viewRecSlideOver" @close-modal="handleCloseViewReciept">
    <div class="w-full flex flex-col content-between">
      <h1 class="text-blue text-2xl">Payment</h1>
      <div v-if="tempBankData.bank_name && temPaymentData">
        <div class="bg-light-gray p-4 my-4 rounded-lg space-y-2">
          <p class="text-gray">Payment method</p>
          <p class="text-blue text-xl">
            {{ tempBankData.bank_name }}
          </p>
        </div>
        <div>
          <img class="rounded-md" :src="temPaymentData.attachment_payment_file" alt="" />
        </div>
      </div>
      <div v-else>
        <p class="text-gray text-base">No payment attach</p>
      </div>
    </div>
  </SideBar>
</template>

<script>
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "@headlessui/vue";
import {
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  UserPlusIcon,
  UserMinusIcon,
  ChevronDownIcon,
  EyeIcon,
} from "@heroicons/vue/24/outline";
import { InboxIcon } from "@heroicons/vue/24/solid";
import userService from "../Services/user.service";
import BasePagination from "../components/BasePagination.vue";
import { useToast } from "vue-toastification";
import SideBar from "../components/SideBar.vue";
import moment from "moment";
export default {
  components: {
    MagnifyingGlassIcon,
    EllipsisVerticalIcon,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    BasePagination,
    UserPlusIcon,
    UserMinusIcon,
    ChevronDownIcon,
    InboxIcon,
    EyeIcon,
    SideBar,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      isLoading: false,
      viewSlideOver: false,
      members: [],
      membersMeta: [],
      params: new URLSearchParams(),
      searchText: "",
      subFilterOption: [],
      subFilter: "",
      statusFilterOption: [
        { kay: "", name: "All" },
        { key: "1", name: "In Review" },
        { key: "-1", name: "Reject" },
      ],
      statusFilter: { kay: "", name: "All" },
      tempData: [],
      viewRecSlideOver: false,
      temPaymentData: [],
      tempBankData: [],
    };
  },
  methods: {
    getMember() {
      this.params.set("whereIn", "-1,1");
      userService.getMembers(this.params).then((response) => {
        this.members = response.data.data;
        this.membersMeta = response.data.meta;
        this.isLoading = false;
      });
    },
    getSubscriptionList() {
      userService.getSubscriptionList().then((response) => {
        this.subFilterOption = response.data.data;
      });
    },
    handleImport() {
      alert("Import button clicked!");
    },
    handleExport() {
      alert("Export Function clicked!");
    },
    handleUpdateStatus(memberId, statusCode) {
      this.$Progress.start();
      let formData = new FormData();
      formData.append("user_id", memberId);
      formData.append("status", statusCode);
      userService
        .storeUpdateStatus(formData)
        .then(() => {
          this.$Progress.finish();
          this.getMember();
          this.getDashBoardSummray();
          this.toast.success("Account status update success");
        })
        .catch(() => {
          this.$Progress.fail();
          this.toast.error("Something went wrong! Could not update status");
        });
    },
    handleSearch() {
      this.params.set("search", this.searchText);
      if (this.statusFilter.key) {
        this.params.set("status", this.statusFilter.key);
      } else {
        this.params.delete("status");
      }
      if (this.subFilter) {
        this.params.set("subscription_id", this.subFilter.id);
      } else {
        this.params.delete("subscription_id");
      }
      this.getMember(this.params.toString());
    },
    handlePagination(value) {
      this.params.set("page", value);
      this.getMember(this.params.toString());
    },
    handleViewSlideOver(data) {
      this.viewSlideOver = !this.viewSlideOver;
      this.tempData = data;
    },
    getDashBoardSummray() {
      userService.getDashboard().then((response) => {
        this.$store.commit("setInboxCount", response.data.unread_inbox);
        this.$store.commit("setNotificationCount", response.data.unread_notification);
        this.$store.commit("setMemberRequestCount", response.data.new_member_request);
        this.$store.commit("setRequestUpdateCount", response.data.member_request_update);
      });
    },
    handleViewReciept(data) {
      this.viewRecSlideOver = true;
      this.temPaymentData = data.latest_subscription || {};
      this.tempBankData = data.latest_subscription.payment || {};
    },
    handleCloseViewReciept() {
      this.viewRecSlideOver = false;
      this.temPaymentData = {};
      this.tempBankData = {};
    },
  },
  created() {
    this.$Progress.start();
    this.moment = moment;
    this.isLoading = true;
    this.getMember();
    this.getSubscriptionList();
  },
  mounted() {
    this.$Progress.finish();
  },
};
</script>

<style scoped>
.square-img {
  aspect-ratio: 1/1;
}
</style>